// Curated physical-therapy exercise catalog (PHYSICAL_THERAPY_PLAN.md Phase 2/3/8,
// D-PT-2/8.1/10/11). Lives in `shared/` so the web SPA can resolve exerciseIds
// client-side without an extra round-trip (D-PT-11; Phase 3 decision, §3.2).
//
// The smart model SELECTS from this vetted list given an injury; it never
// free-generates exercises (D-PT-2). `description` is a Markdown template literal
// rendered via react-markdown (D-PT-10). Images live at web/public/exercises/<id>.*;
// `imageRef` is the public-relative path (D-PT-11).
//
// ─────────────────────────────────────────────────────────────────────────────
// HOW TO ADD A NEW EXERCISE
// ─────────────────────────────────────────────────────────────────────────────
// 1. Copy an existing PtExercise object as a template.
// 2. Set a globally-unique kebab-case `id` (e.g. "shoulder-pendulum").
// 3. Fill every required field (id, name, description, imageRef, targetRegions,
//    indications, frequencyRecommended, expectedTimeToResults).
// 4. Add a placeholder image at web/public/exercises/<id>.svg (labeled SVG or gray PNG).
//    Record the image source in a comment on the `imageRef` line.
// 5. Set evidenceNote: "DRAFT — needs clinical review before use".
// 3b. Tag primaryMuscles / secondaryMuscles and their anatomical ids. Every anatomical
//     id's parent group (MUSCLE_META[id].group) must also appear in the matching coarse
//     array — `npm run validate:exercises` enforces this.
// 3c. Set `purpose` (TRAINER_ACCURACY_STAGE_1_2_PLAN D-TA-1/F2): "mobility" for
//     stretches/passive ROM, "motor-control" for balance/activation drills,
//     "resistance" for loaded/strength work. Required on every entry —
//     `npm run validate:exercises` enforces it.
// 6. Run `npm run validate:exercises` — it must exit 0 before committing.
//
// REVIEW CHECKLIST (to remove the draft flag):
// ─────────────────────────────────────────────
// • Indications and contraindications reviewed by a licensed clinician.
// • Frequency and expected-time-to-results confirmed against published guidelines.
// • Cautions cover all significant patient-safety considerations.
// • `purpose` confirmed by a clinician — a mistagged stretch or balance drill as
//   "resistance" would let it satisfy a strength target it never trained.
// • Description steps match the named exercise precisely; no misleading cues.
// • evidenceNote line deleted (or replaced with citation).
//
// IMAGE LICENSING NOTE:
// ─────────────────────
// Only use images you own, have licensed, or that are Creative Commons (CC0/CC BY).
// Document the source in a comment on the `imageRef` line, e.g.:
//   imageRef: "/exercises/shoulder-pendulum.svg", // own illustration

import type { PtExercise } from "./physicalTherapy.js";

export const PT_EXERCISES: PtExercise[] = [
  // ─── SHOULDER / ROTATOR CUFF ─────────────────────────────────────────────

  {
    id: "shoulder-pendulum",
    purpose: "mobility",
    name: "Pendulum Swing",
    description: `## Pendulum Swing

Gently mobilizes the shoulder without loading the joint, reducing stiffness and improving range of motion in early recovery.

### Steps
1. **Lean forward** at the waist, supporting yourself with your uninvolved arm on a table.
2. Let the affected arm **hang straight down**, fully relaxed — do not grip or tense the shoulder.
3. Using your torso — *not* your shoulder muscles — sway gently to make the arm swing in small **clockwise circles** (10 reps), then **counter-clockwise** (10 reps).
4. Repeat moving **front-to-back** (10 reps) and then **side-to-side** (10 reps).
5. Keep circles small (≈ 30 cm diameter); gravity and momentum do all the work.

### Tips
- **Relax completely** — active muscle use defeats the purpose.
- Gradually increase circle size over days as pain allows.
- Do not swing through sharp pain; mild ache is acceptable.`,
    imageRef: "/exercises/shoulder-pendulum.svg", // own placeholder illustration
    targetRegions: ["shoulder", "arm"],
    indications: ["rotator cuff strain", "shoulder stiffness", "overuse", "frozen shoulder", "shoulder impingement"],
    contraindications: ["suspected fracture", "dislocation", "acute shoulder instability"],
    frequencyRecommended: "2×/day, 1–2 minutes per direction",
    expectedTimeToResults: "2–4 weeks",
    cautions: [
      "Stop if you feel sharp pain.",
      "Keep the arm relaxed — don't actively lift it.",
      "Avoid if the shoulder is acutely dislocated or suspected fractured.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["shoulders"],
    primaryMuscleIds: ["delt-anterior", "delt-lateral", "delt-posterior"],
    secondaryMuscles: ["back"],
    secondaryMuscleIds: ["rotator-cuff"],
  },

  {
    id: "shoulder-doorway-stretch",
    purpose: "mobility",
    name: "Doorway Pec Stretch",
    description: `## Doorway Pec Stretch

Opens the chest and anterior shoulder, countering the forward-rounded posture that worsens rotator-cuff and impingement pain.

### Steps
1. Stand in an open doorway. Place both forearms vertically on the door frame, **elbows at 90°**.
2. Step one foot forward and gently **lean your body through the doorway** until you feel a comfortable stretch across the chest and front of the shoulders.
3. Hold for **20–30 seconds**, breathing slowly. Do not force it.
4. Step back, rest 10 seconds, then repeat.

### Tips
- You can vary arm height (low, mid, high) to target slightly different fibres of the pec.
- Keep your chin tucked and core lightly engaged — avoid arching the lower back.`,
    imageRef: "/exercises/shoulder-doorway-stretch.svg", // own placeholder illustration
    targetRegions: ["shoulder", "chest"],
    indications: ["rotator cuff strain", "shoulder impingement", "forward posture", "overuse", "shoulder tightness"],
    contraindications: ["suspected fracture", "acute shoulder instability", "recent shoulder surgery"],
    frequencyRecommended: "2–3×/day, 3 reps × 20–30 s",
    expectedTimeToResults: "2–4 weeks",
    cautions: [
      "Do not force beyond comfortable range.",
      "Stop if numbness or tingling occurs in the arm.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["chest"],
    primaryMuscleIds: ["pec-clavicular", "pec-sternal"],
    secondaryMuscles: ["shoulders"],
    secondaryMuscleIds: ["delt-anterior"],
  },

  {
    id: "shoulder-wall-slides",
    purpose: "motor-control",
    name: "Wall Slides",
    description: `## Wall Slides

Trains scapular upward rotation and lower-trapezius activation to restore healthy shoulder mechanics.

### Steps
1. Stand with your **back flat against a smooth wall**, feet about 15 cm from the baseboard.
2. Press your elbows, wrists, and the backs of your hands against the wall, arms bent to **90°** (like a goalpost position).
3. Slowly **slide your arms upward** along the wall, maintaining contact with both arms and the wall, until your arms are nearly straight overhead.
4. Pause 2 seconds at the top, then slowly **slide back down** to the start.

### Tips
- Keep your lower back pressed to the wall throughout — do not let it arch.
- Move only as high as you can while keeping both arms in contact with the wall.
- Pain-free range only; reduce height if needed.`,
    imageRef: "/exercises/shoulder-wall-slides.svg", // own placeholder illustration
    targetRegions: ["shoulder", "back-upper"],
    indications: ["rotator cuff strain", "shoulder impingement", "scapular dyskinesis", "shoulder instability", "overuse"],
    contraindications: ["suspected fracture", "acute dislocation"],
    frequencyRecommended: "1–2×/day, 10–15 reps",
    expectedTimeToResults: "3–6 weeks",
    cautions: [
      "Stop if you feel sharp pain or the arm cannot stay on the wall.",
      "Progress height gradually; never force the range.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["shoulders", "back"],
    primaryMuscleIds: ["delt-anterior", "delt-lateral", "traps-lower", "traps-upper"],
    secondaryMuscles: ["chest"],
    secondaryMuscleIds: ["serratus-anterior"],
  },

  {
    id: "shoulder-external-rotation-band",
    purpose: "resistance",
    name: "Resisted External Rotation (Band)",
    description: `## Resisted External Rotation (Band)

Strengthens the infraspinatus and teres minor — the key stabilizers in rotator-cuff injuries — using a resistance band.

### Steps
1. Attach a resistance band to a door handle or fixed point at elbow height.
2. Stand sideways to the anchor, affected arm closest. Hold the band with the affected hand.
3. Tuck a small folded towel between your elbow and side to keep the elbow at **90°**.
4. Starting with the forearm pointing toward the anchor, **rotate your forearm outward** (away from your body) against the band resistance, keeping the elbow tucked.
5. Hold the end position for 2 seconds, then return slowly.

### Tips
- Motion comes from the shoulder — keep the elbow pinned to your side.
- Use a light band; this is a precision exercise, not a strength exercise.
- If the towel falls out, the elbow is drifting away from the body.`,
    imageRef: "/exercises/shoulder-external-rotation-band.svg", // own placeholder illustration
    targetRegions: ["shoulder", "arm"],
    indications: ["rotator cuff strain", "shoulder impingement", "rotator cuff tear (partial)", "shoulder instability"],
    contraindications: ["suspected fracture", "full-thickness rotator cuff tear (needs surgical eval)", "acute dislocation"],
    frequencyRecommended: "1×/day, 3 sets × 10–15 reps",
    expectedTimeToResults: "4–8 weeks",
    cautions: [
      "Use only light resistance — heavy loads worsen impingement.",
      "Stop if you feel pain at the front of the shoulder.",
      "Consult a clinician before starting if a tear has been diagnosed.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["back"],
    primaryMuscleIds: ["rotator-cuff"],
    secondaryMuscles: ["shoulders", "back"],
    secondaryMuscleIds: ["delt-posterior", "rhomboids"],
  },

  // ─── LOWER BACK ───────────────────────────────────────────────────────────

  {
    id: "lower-back-pelvic-tilt",
    purpose: "motor-control",
    name: "Pelvic Tilt",
    description: `## Pelvic Tilt

Activates the deep core stabilizers and releases lower-back tension — a foundational exercise for non-specific low back pain.

### Steps
1. Lie on your back with **knees bent**, feet flat on the floor, arms at your sides.
2. Gently **flatten your lower back** against the floor by tightening your abdominal muscles and tilting your pelvis slightly upward (as if tucking your tailbone under).
3. Hold for **5 seconds**, breathing normally — do not hold your breath.
4. Relax fully, then repeat.

### Tips
- The movement is small and subtle; do not lift the hips off the floor.
- Focus on muscle engagement, not range of motion.`,
    imageRef: "/exercises/lower-back-pelvic-tilt.svg", // own placeholder illustration
    targetRegions: ["back-lower"],
    indications: ["lower back strain", "non-specific low back pain", "overuse", "lumbar muscle spasm"],
    contraindications: ["suspected fracture", "acute disc herniation with leg numbness"],
    frequencyRecommended: "2×/day, 10 reps × 5 s hold",
    expectedTimeToResults: "2–6 weeks",
    cautions: [
      "Move slowly.",
      "Stop if pain radiates down the leg.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["core"],
    primaryMuscleIds: ["transverse-abdominis", "rectus-abdominis"],
    secondaryMuscles: ["glutes", "back"],
    secondaryMuscleIds: ["glute-maximus", "erector-spinae", "multifidus"],
  },

  {
    id: "lower-back-cat-cow",
    purpose: "mobility",
    name: "Cat-Cow Stretch",
    description: `## Cat-Cow Stretch

Mobilizes the entire lumbar and thoracic spine through gentle flexion-extension cycling, relieving stiffness and muscle spasm.

### Steps
1. Start on your hands and knees (table-top position), wrists directly under shoulders, knees under hips.
2. **Cat:** On an exhale, round your back toward the ceiling, tuck your chin to your chest, and tuck your tailbone under. Hold 2–3 seconds.
3. **Cow:** On an inhale, let your belly drop toward the floor, lift your head and tailbone gently, creating a mild arch. Hold 2–3 seconds.
4. Flow smoothly between the two positions, coordinating with your breath.

### Tips
- Move slowly and breathe deliberately.
- Stop at any point that causes sharp pain.
- Limit Cow (extension) if it worsens leg symptoms.`,
    imageRef: "/exercises/lower-back-cat-cow.svg", // own placeholder illustration
    targetRegions: ["back-lower", "back-upper"],
    indications: ["lower back strain", "non-specific low back pain", "lumbar stiffness", "overuse", "facet joint irritation"],
    contraindications: ["suspected fracture", "severe spinal stenosis with extension intolerance"],
    frequencyRecommended: "2×/day, 10 slow cycles",
    expectedTimeToResults: "1–4 weeks",
    cautions: [
      "Skip the Cow (arching) phase if it worsens leg pain.",
      "Never force the range of motion.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["back"],
    // Cat-cow is segment-by-segment spinal motion, which is the multifidus' job.
    primaryMuscleIds: ["erector-spinae", "multifidus"],
    secondaryMuscles: ["core"],
    secondaryMuscleIds: ["rectus-abdominis", "transverse-abdominis"],
  },

  {
    id: "lower-back-knee-to-chest",
    purpose: "mobility",
    name: "Knee-to-Chest Stretch",
    description: `## Knee-to-Chest Stretch

Gently lengthens the lumbar extensors and piriformis, decompressing the lower spine and reducing muscle tightness.

### Steps
1. Lie on your back with both knees bent, feet flat on the floor.
2. Bring **one knee up** toward your chest, clasping both hands behind the thigh (or on the shin).
3. Gently pull the knee in until you feel a comfortable stretch in the lower back and buttock. Hold **20–30 seconds**.
4. Lower slowly, then repeat on the other side.
5. Option: pull both knees in together for a bilateral stretch.

### Tips
- Keep the opposite foot flat on the floor (or leg straight) for stability.
- Breathe slowly and let the muscles relax into the stretch.`,
    imageRef: "/exercises/lower-back-knee-to-chest.svg", // own placeholder illustration
    targetRegions: ["back-lower", "hip"],
    indications: ["lower back strain", "lumbar stiffness", "overuse", "sacroiliac joint pain", "non-specific low back pain"],
    contraindications: ["suspected fracture", "acute disc herniation with significant leg symptoms"],
    frequencyRecommended: "2×/day, 3 reps × 20–30 s each side",
    expectedTimeToResults: "1–3 weeks",
    cautions: [
      "Stop if you feel leg numbness or tingling.",
      "Avoid if pulling the knee up worsens radiating pain.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["back"],
    primaryMuscleIds: ["erector-spinae"],
    secondaryMuscles: ["glutes"],
    secondaryMuscleIds: ["glute-maximus"],
  },

  {
    id: "lower-back-bird-dog",
    purpose: "motor-control",
    name: "Bird-Dog",
    description: `## Bird-Dog

Builds lumbar-spine stability by training the deep core and gluteal muscles to work together while the spine stays neutral.

### Steps
1. Start on hands and knees, wrists under shoulders, knees under hips. Find a neutral spine (small natural arch, not flat or over-arched).
2. Simultaneously extend your **right arm** straight forward and your **left leg** straight back, keeping hips level.
3. Hold for **5–10 seconds**, then return slowly to the start.
4. Repeat on the opposite side (left arm, right leg). That is 1 rep.

### Tips
- Imagine balancing a glass of water on your lower back — do not let the hips rotate.
- Extend to a comfortable height; do not over-lift the arm or leg.
- Engage your core gently before each repetition.`,
    imageRef: "/exercises/lower-back-bird-dog.svg", // own placeholder illustration
    targetRegions: ["back-lower"],
    indications: ["lower back strain", "core weakness", "lumbar instability", "non-specific low back pain", "overuse"],
    contraindications: ["suspected fracture", "severe acute back pain that prevents quadruped positioning"],
    frequencyRecommended: "1–2×/day, 10 reps each side",
    expectedTimeToResults: "3–6 weeks",
    cautions: [
      "Keep the movement slow and controlled.",
      "Stop if balance is unsafe or if back pain increases.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["core", "back"],
    // A bird dog is a hold, not a movement: the multifidus is what keeps each
    // spinal segment still while the opposite limbs move.
    primaryMuscleIds: ["transverse-abdominis", "obliques", "erector-spinae", "multifidus"],
    secondaryMuscles: ["glutes", "shoulders"],
    secondaryMuscleIds: ["glute-maximus", "delt-anterior"],
  },

  {
    id: "lower-back-bridge",
    purpose: "resistance",
    name: "Glute Bridge",
    description: `## Glute Bridge

Activates the gluteus maximus and hamstrings to offload the lumbar spine, a key step in low-back rehabilitation.

### Steps
1. Lie on your back, **knees bent** at about 90°, feet flat, hip-width apart.
2. Press your feet into the floor and **lift your hips** toward the ceiling until your body forms a straight line from knees to shoulders.
3. Squeeze your glutes at the top. Hold for **2–3 seconds**.
4. Lower slowly — vertebra by vertebra — back to the floor.

### Tips
- Do not hyperextend the lower back at the top; keep the core lightly braced.
- Feet should stay flat — avoid rising onto the toes.
- Progress to single-leg bridges when the bilateral bridge feels easy.`,
    imageRef: "/exercises/lower-back-bridge.svg", // own placeholder illustration
    targetRegions: ["back-lower", "hip", "leg"],
    indications: ["lower back strain", "gluteal weakness", "non-specific low back pain", "overuse", "sacroiliac joint pain"],
    contraindications: ["suspected fracture", "acute disc herniation with significant leg symptoms"],
    frequencyRecommended: "1–2×/day, 3 sets × 10–15 reps",
    expectedTimeToResults: "3–6 weeks",
    cautions: [
      "Do not push through lower-back pain; reduce range if needed.",
      "Avoid if any leg symptoms worsen during the exercise.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["glutes"],
    primaryMuscleIds: ["glute-maximus"],
    secondaryMuscles: ["hamstrings", "core", "back"],
    secondaryMuscleIds: ["biceps-femoris", "semitendinosus", "semimembranosus", "transverse-abdominis", "erector-spinae", "multifidus"],
  },

  // ─── KNEE / RUNNER'S KNEE ─────────────────────────────────────────────────

  {
    id: "knee-quad-set",
    purpose: "motor-control",
    name: "Quad Set",
    description: `## Quad Set

Re-engages the quadriceps without bending the knee — the safest first step after acute knee injury or post-operative inhibition.

### Steps
1. Sit or lie with the affected **leg straight** out in front of you.
2. **Tighten the thigh muscle** on top, pressing the back of the knee gently down toward the floor (imagine squishing a small towel under the back of the knee).
3. Hold for **5 seconds**, keeping the kneecap pulled slightly upward.
4. Relax fully, then repeat.

### Tips
- There should be no pain — only a muscle-tightening sensation.
- You can place a small rolled towel under the knee for comfort.`,
    imageRef: "/exercises/knee-quad-set.svg", // own placeholder illustration
    targetRegions: ["leg"],
    indications: ["runner's knee", "patellofemoral pain", "knee sprain", "overuse", "post-operative quad inhibition"],
    contraindications: ["suspected fracture", "locked knee", "acute ligament tear"],
    frequencyRecommended: "2–3×/day, 10 reps × 5 s hold",
    expectedTimeToResults: "3–6 weeks",
    cautions: [
      "Keep movements gentle.",
      "Stop if the knee swells or gives way.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["quads"],
    primaryMuscleIds: ["vastus-medialis", "rectus-femoris", "vastus-lateralis"],
    secondaryMuscles: [],
    secondaryMuscleIds: [],
  },

  {
    id: "knee-straight-leg-raise",
    purpose: "resistance",
    name: "Straight-Leg Raise",
    description: `## Straight-Leg Raise

Strengthens the quadriceps and hip flexors while keeping the knee fully protected — a safe early-stage knee exercise.

### Steps
1. Lie on your back. Bend the uninvolved knee, foot flat on the floor for support.
2. **Tighten the thigh** of the affected (straight) leg so the knee is fully extended and locked.
3. Keeping the leg straight, **raise it to the height of the opposite knee** (about 45°).
4. Hold 2 seconds at the top, then lower slowly.

### Tips
- Lock the knee fully before and throughout the lift — this protects the joint.
- Keep the movement slow; momentum defeats the purpose.`,
    imageRef: "/exercises/knee-straight-leg-raise.svg", // own placeholder illustration
    targetRegions: ["leg", "hip"],
    indications: ["runner's knee", "patellofemoral pain", "knee sprain", "overuse", "quadriceps weakness"],
    contraindications: ["suspected fracture", "acute hip flexor strain", "locked knee"],
    frequencyRecommended: "1–2×/day, 3 sets × 10–15 reps",
    expectedTimeToResults: "3–6 weeks",
    cautions: [
      "Fully extend and tighten the knee before lifting.",
      "Stop if pain increases with the lift.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["quads"],
    primaryMuscleIds: ["rectus-femoris", "vastus-medialis", "vastus-lateralis"],
    // The quads hold the knee locked; the hip flexors are what raise the leg.
    secondaryMuscles: ["core", "hip-flexors"],
    secondaryMuscleIds: ["rectus-abdominis", "transverse-abdominis", "psoas-major", "iliacus"],
  },

  {
    id: "knee-clamshell",
    purpose: "resistance",
    name: "Clamshell",
    description: `## Clamshell

Strengthens the hip abductors (gluteus medius), which stabilize the knee and reduce patellofemoral stress.

### Steps
1. Lie on your **uninvolved side**, hips and knees bent to about 45°, feet stacked.
2. Keeping your **feet together**, slowly rotate the top knee toward the ceiling — like a clamshell opening — until about hip height.
3. Pause 2 seconds, then lower the knee slowly. Do not let the pelvis roll backward.

### Tips
- A resistance band around the thighs increases difficulty once the movement feels easy.
- If your hip rolls back, you've gone too high — reduce the range.`,
    imageRef: "/exercises/knee-clamshell.svg", // own placeholder illustration
    targetRegions: ["leg", "hip"],
    indications: ["runner's knee", "patellofemoral pain", "IT band syndrome", "hip weakness", "overuse"],
    contraindications: ["suspected fracture", "acute hip injury"],
    frequencyRecommended: "1–2×/day, 3 sets × 15 reps each side",
    expectedTimeToResults: "4–6 weeks",
    cautions: [
      "Keep the pelvis still — do not rock backward.",
      "Stop if hip or knee pain increases.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    // The coarse primary array must stay exactly ["glutes"] — scripts/checkPtMuscleLoad.ts
    // Case 7 asserts on it. Abductor content belongs in the secondary arrays.
    primaryMuscles: ["glutes"],
    primaryMuscleIds: ["glute-medius", "glute-minimus"],
    secondaryMuscles: ["hip-abductors"],
    secondaryMuscleIds: ["tfl"],
  },

  {
    id: "knee-step-down",
    purpose: "resistance",
    name: "Step-Down (Eccentric Control)",
    description: `## Step-Down (Eccentric Control)

Trains eccentric quadriceps control and patellar tracking — essential for runner's knee and return-to-sport readiness.

### Steps
1. Stand on a **step** (or a thick book), affected leg only, with a slight knee bend.
2. Slowly **lower the opposite heel** toward the floor by bending only the standing knee. Control the descent — 3–4 seconds.
3. Tap the heel lightly (do not load it), then push back up to the start through the standing leg.

### Tips
- Keep the knee tracking in line with the second toe — do not let it cave inward.
- Start with a low step (5–10 cm) and progress height as control improves.
- If the knee shakes or dives inward, regress to clamshells and quad sets first.`,
    imageRef: "/exercises/knee-step-down.svg", // own placeholder illustration
    targetRegions: ["leg"],
    indications: ["runner's knee", "patellofemoral pain", "IT band syndrome", "quadriceps weakness", "return to sport"],
    contraindications: ["suspected fracture", "acute ligament tear", "significant knee swelling"],
    frequencyRecommended: "1×/day, 3 sets × 10 reps",
    expectedTimeToResults: "4–8 weeks",
    cautions: [
      "Start with a low step; increase height gradually.",
      "Stop if the knee dives inward or pain increases.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["quads"],
    primaryMuscleIds: ["vastus-medialis", "vastus-lateralis", "rectus-femoris"],
    secondaryMuscles: ["glutes", "calves"],
    secondaryMuscleIds: ["glute-maximus", "glute-medius", "glute-minimus", "soleus"],
  },

  // ─── ANKLE SPRAIN ─────────────────────────────────────────────────────────

  {
    id: "ankle-alphabet",
    purpose: "mobility",
    name: "Ankle Alphabet",
    description: `## Ankle Alphabet

Early-stage ankle mobility exercise that moves the joint through its full range without load, reducing stiffness after a sprain.

### Steps
1. Sit in a chair with the **affected foot elevated** (cross the ankle over the opposite knee, or use a stool).
2. Using your **big toe as a pen**, trace each letter of the alphabet in the air — capital letters, nice and large.
3. Move only the ankle; keep the leg still.

### Tips
- Do both uppercase and lowercase for maximum range-of-motion variety.
- Mild aching is expected; sharp pain means stop.
- Progress to standing ankle circles once weight-bearing is comfortable.`,
    imageRef: "/exercises/ankle-alphabet.svg", // own placeholder illustration
    targetRegions: ["foot"],
    indications: ["ankle sprain", "ankle stiffness", "lateral ankle sprain", "overuse"],
    contraindications: ["suspected fracture", "acute ankle dislocation"],
    frequencyRecommended: "3–4×/day",
    expectedTimeToResults: "1–3 weeks",
    cautions: [
      "Non-weight-bearing exercise only — sit down.",
      "Stop if sharp pain occurs.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["calves"],
    // Tracing letters takes the ankle through eversion and inversion as well as
    // dorsi/plantarflexion, so the two frontal-plane muscles belong here too.
    primaryMuscleIds: ["tibialis-anterior", "gastrocnemius", "soleus", "peroneals", "tibialis-posterior"],
    secondaryMuscles: [],
    secondaryMuscleIds: [],
  },

  {
    id: "ankle-towel-calf-stretch",
    purpose: "mobility",
    name: "Towel Calf Stretch (Seated)",
    description: `## Towel Calf Stretch (Seated)

Restores dorsiflexion range of motion, which is commonly lost after an ankle sprain and must be regained to prevent re-injury.

### Steps
1. Sit on the floor with the affected **leg straight** out in front of you.
2. Loop a towel or strap around the ball of the foot.
3. Gently **pull the towel toward you**, dorsiflexing the ankle (foot toward shin), until you feel a comfortable stretch in the calf.
4. Hold **20–30 seconds**, then relax.

### Tips
- Keep the knee straight to target the gastrocnemius; bend it slightly to target the soleus.
- Never yank — apply gentle, sustained tension.`,
    imageRef: "/exercises/ankle-towel-calf-stretch.svg", // own placeholder illustration
    targetRegions: ["foot", "leg"],
    indications: ["ankle sprain", "calf tightness", "Achilles tightness", "lateral ankle sprain"],
    contraindications: ["suspected fracture", "acute Achilles rupture"],
    frequencyRecommended: "3×/day, 3 reps × 20–30 s",
    expectedTimeToResults: "2–4 weeks",
    cautions: [
      "Do not force the stretch; sustained gentle tension only.",
      "Stop if ankle pain increases.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["calves"],
    primaryMuscleIds: ["gastrocnemius", "soleus"],
    secondaryMuscles: [],
    secondaryMuscleIds: [],
  },

  {
    id: "ankle-single-leg-balance",
    purpose: "motor-control",
    name: "Single-Leg Balance",
    description: `## Single-Leg Balance

Restores proprioception and neuromuscular control — the most important factors in preventing re-sprain.

### Steps
1. Stand near a wall or chair for safety.
2. Shift weight onto the **affected foot**, lifting the other foot slightly off the floor.
3. Balance for **30 seconds** while keeping the ankle stable and the knee slightly bent (not locked).
4. Rest 15 seconds, then repeat.

### **Progression:**
- Eyes open on flat ground → eyes closed → unstable surface (folded towel/balance disc).
- Add small arm movements once steady.

### Tips
- It is normal to wobble — the wobbling is the training stimulus.
- If balance is unsafe, hold the wall lightly and reduce contact over time.`,
    imageRef: "/exercises/ankle-single-leg-balance.svg", // own placeholder illustration
    targetRegions: ["foot", "leg"],
    indications: ["ankle sprain", "chronic ankle instability", "proprioception deficit", "lateral ankle sprain"],
    contraindications: ["suspected fracture", "significant weight-bearing pain", "acute dislocation"],
    frequencyRecommended: "2×/day, 3 reps × 30 s",
    expectedTimeToResults: "3–6 weeks",
    cautions: [
      "Stand near support in case of loss of balance.",
      "Begin on flat firm ground before progressing to unstable surfaces.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["calves"],
    // Balancing on one foot is exactly the job of the peroneals and the arch:
    // they are what stop the ankle rolling and what grip the floor.
    primaryMuscleIds: ["soleus", "tibialis-anterior", "gastrocnemius", "peroneals", "plantar-intrinsics"],
    secondaryMuscles: ["glutes", "core", "hip-abductors", "calves"],
    secondaryMuscleIds: ["glute-medius", "glute-minimus", "transverse-abdominis", "tfl", "tibialis-posterior"],
  },

  {
    id: "ankle-heel-raises",
    purpose: "resistance",
    name: "Heel Raises (Calf Press)",
    description: `## Heel Raises (Calf Press)

Builds calf strength and Achilles tendon load tolerance — critical for returning to walking, running, and jumping after an ankle sprain.

### Steps
1. Stand on **both feet** near a wall or counter for light balance support.
2. Rise up onto the **balls of both feet** as high as comfortable, squeezing the calves.
3. Hold the top position for 2 seconds, then lower slowly (3–4 seconds) back to flat.

### **Progression:**
- Both feet → affected foot only → affected foot on a small step (increased range).

### Tips
- The slow lowering (eccentric phase) is the most important part.
- Do not let the ankle roll outward during the raise.`,
    imageRef: "/exercises/ankle-heel-raises.svg", // own placeholder illustration
    targetRegions: ["foot", "leg"],
    indications: ["ankle sprain", "Achilles tightness", "calf weakness", "chronic ankle instability"],
    contraindications: ["suspected fracture", "acute Achilles rupture or significant Achilles tendinopathy"],
    frequencyRecommended: "1–2×/day, 3 sets × 15 reps",
    expectedTimeToResults: "3–6 weeks",
    cautions: [
      "Lower slowly — do not bounce.",
      "Stop if Achilles pain worsens.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["calves"],
    primaryMuscleIds: ["gastrocnemius", "soleus"],
    // Rising onto the toes without letting the foot roll out is the tib post's
    // and the arch muscles' work, not the calf's.
    secondaryMuscles: ["calves"],
    secondaryMuscleIds: ["tibialis-posterior", "plantar-intrinsics"],
  },

  {
    id: "ankle-resisted-eversion",
    purpose: "resistance",
    name: "Resisted Eversion (Band)",
    description: `## Resisted Eversion (Band)

Strengthens the peroneal muscles that protect the lateral ankle from re-sprain — the most evidence-supported exercise in lateral-ankle rehabilitation.

### Steps
1. Sit in a chair. Tie a resistance band in a loop and place it around **both feet** (or anchor one end to a fixed object).
2. Starting with the affected foot pointing slightly inward, **rotate it outward** (eversion) against the band resistance.
3. Move slowly, hold 2 seconds at the end range, then return slowly.

### Tips
- The motion is foot outward only — keep the leg still.
- Use a light band initially; progress resistance as strength improves.
- Pair with resisted inversion (same setup, foot moves inward) to balance both sides.`,
    imageRef: "/exercises/ankle-resisted-eversion.svg", // own placeholder illustration
    targetRegions: ["foot", "leg"],
    indications: ["ankle sprain", "lateral ankle sprain", "chronic ankle instability", "peroneal weakness"],
    contraindications: ["suspected fracture", "acute dislocation", "peroneal tendon rupture"],
    frequencyRecommended: "1–2×/day, 3 sets × 15 reps",
    expectedTimeToResults: "4–8 weeks",
    cautions: [
      "Use only light resistance initially.",
      "Stop if sharp lateral ankle pain occurs.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["calves"],
    primaryMuscleIds: ["peroneals"],
    // The description pairs this with resisted inversion, which is the tib post.
    secondaryMuscles: ["calves"],
    secondaryMuscleIds: ["tibialis-posterior"],
  },

  // ─── CERVICAL / NECK ──────────────────────────────────────────────────────

  {
    id: "neck-chin-tuck",
    purpose: "motor-control",
    name: "Chin Tuck",
    description: `## Chin Tuck

Activates the deep cervical flexors and corrects forward-head posture — the foundational exercise for most neck pain presentations.

### Steps
1. Sit or stand with your back straight.
2. Without tilting the head up or down, **gently draw your chin straight back** — as if making a subtle double chin.
3. Hold for **5 seconds**. You should feel a mild stretch at the base of the skull and a gentle activation of the front of the neck.
4. Relax and repeat.

### Tips
- Think "chin back", not "chin down" — the movement is horizontal, not a nod.
- Start with very small movements; you can increase depth over days.
- Can also be done lying on your back (easier starting position).`,
    imageRef: "/exercises/neck-chin-tuck.svg", // own placeholder illustration
    targetRegions: ["neck", "head"],
    indications: ["neck strain", "cervical muscle spasm", "forward head posture", "cervicogenic headache", "overuse", "whiplash"],
    contraindications: ["suspected cervical fracture", "cervical instability", "myelopathy"],
    frequencyRecommended: "3–4×/day, 10 reps × 5 s hold",
    expectedTimeToResults: "2–4 weeks",
    cautions: [
      "Stop if dizziness, arm numbness, or tingling occurs.",
      "Move gently — this is a subtle motion.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["neck"],
    primaryMuscleIds: ["deep-cervical-flexors"],
    secondaryMuscles: ["neck"],
    secondaryMuscleIds: ["sternocleidomastoid", "scalenes"],
  },

  {
    id: "neck-side-bend-stretch",
    purpose: "mobility",
    name: "Cervical Side-Bend Stretch",
    description: `## Cervical Side-Bend Stretch

Lengthens the cervical lateral flexors and upper trapezius, relieving the common tension pattern along the side of the neck.

### Steps
1. Sit upright in a chair. Rest the **same-side hand** on the seat to anchor the shoulder down.
2. Slowly **tilt your head toward the opposite shoulder** until you feel a comfortable stretch along the side of the neck.
3. For a deeper stretch, gently place the **opposite hand on the temple** (not the back of the head) and apply very light overpressure.
4. Hold **20–30 seconds**, breathing slowly. Repeat on both sides.

### Tips
- Keep the shoulder anchored (hand on seat) to increase the stretch.
- Never pull on the back of the head — apply overpressure at the temple only.`,
    imageRef: "/exercises/neck-side-bend-stretch.svg", // own placeholder illustration
    targetRegions: ["neck", "shoulder"],
    indications: ["neck strain", "upper trapezius tightness", "cervical muscle spasm", "overuse", "cervicogenic headache"],
    contraindications: ["suspected cervical fracture", "cervical instability", "acute disc herniation with arm symptoms"],
    frequencyRecommended: "2–3×/day, 3 reps × 20–30 s each side",
    expectedTimeToResults: "2–4 weeks",
    cautions: [
      "Stop if arm tingling or numbness occurs.",
      "Apply only gentle overpressure — never force the stretch.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["neck"],
    primaryMuscleIds: ["sternocleidomastoid", "scalenes", "levator-scapulae"],
    secondaryMuscles: ["back"],
    secondaryMuscleIds: ["traps-upper"],
  },

  {
    id: "neck-shoulder-shrug",
    purpose: "resistance",
    name: "Shoulder Shrug",
    description: `## Shoulder Shrug

Re-coordinates the upper trapezius and levator scapulae through a controlled elevation-and-release cycle, reducing chronic neck-shoulder tension.

### Steps
1. Sit or stand upright, arms relaxed at your sides.
2. **Slowly shrug both shoulders up** toward your ears as high as comfortable, hold 3 seconds.
3. **Release slowly** — take 3–4 seconds to lower the shoulders fully.
4. Focus on the release: let the shoulders drop completely, feeling the tension leave.

### Tips
- The slow release is more therapeutic than the lift.
- You can add a backward roll (shrug up, roll back, press down) to open the chest.`,
    imageRef: "/exercises/neck-shoulder-shrug.svg", // own placeholder illustration
    targetRegions: ["neck", "shoulder"],
    indications: ["neck strain", "upper trapezius tightness", "cervical muscle spasm", "overuse", "forward posture"],
    contraindications: ["suspected cervical fracture", "acute cervical disc with arm symptoms"],
    frequencyRecommended: "3×/day, 10 slow reps",
    expectedTimeToResults: "1–3 weeks",
    cautions: [
      "Stop if shrugging causes arm pain or numbness.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    // A shrug genuinely IS an upper-trapezius movement, so the primary tag stands.
    primaryMuscles: ["back"],
    primaryMuscleIds: ["traps-upper"],
    secondaryMuscles: ["shoulders", "neck"],
    secondaryMuscleIds: ["delt-lateral", "levator-scapulae"],
  },

  {
    id: "neck-upper-trap-stretch",
    purpose: "mobility",
    name: "Upper-Trapezius Stretch",
    description: `## Upper-Trapezius Stretch

Targets the upper trapezius belly — a common pain generator in neck strain, cervicogenic headache, and desk-worker overuse.

### Steps
1. Sit upright. With one hand, **grip the seat** of your chair to anchor the shoulder.
2. With the opposite hand on top of the head, gently tilt your head diagonally — **ear toward the opposite knee** (not just ear to shoulder).
3. You should feel the stretch from the side of the neck down to the top of the shoulder blade.
4. Hold **30 seconds**, then switch sides.

### Tips
- The "diagonal" direction (not straight side-bend) isolates the upper trap more specifically.
- Apply pressure with the hand on the head, not a pull — think weight, not force.`,
    imageRef: "/exercises/neck-upper-trap-stretch.svg", // own placeholder illustration
    targetRegions: ["neck", "shoulder", "back-upper"],
    indications: ["neck strain", "upper trapezius tightness", "cervicogenic headache", "overuse", "tension headache"],
    contraindications: ["suspected cervical fracture", "cervical instability", "acute disc herniation with arm symptoms"],
    frequencyRecommended: "2–3×/day, 3 reps × 30 s each side",
    expectedTimeToResults: "2–4 weeks",
    cautions: [
      "Stop if arm numbness or tingling occurs.",
      "Never yank the head — gentle sustained pressure only.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["back", "neck"],
    primaryMuscleIds: ["traps-upper", "levator-scapulae"],
    secondaryMuscles: [],
    secondaryMuscleIds: [],
  },

  {
    id: "neck-gentle-rotation",
    purpose: "mobility",
    name: "Gentle Cervical Rotation",
    description: `## Gentle Cervical Rotation

Restores rotational range of motion in the cervical spine through slow, controlled active movement, reducing stiffness after strain or whiplash.

### Steps
1. Sit upright with both feet flat on the floor.
2. Keeping your chin level, **slowly turn your head to the right** as far as comfortable (not to pain). Hold 2–3 seconds.
3. **Slowly return to center**, then rotate to the left. Hold 2–3 seconds.
4. Continue alternating sides in a slow, rhythmic pattern.

### Tips
- The movement should be smooth — no jerking or forcing.
- If one direction is more restricted or painful, stay within the pain-free range and do not push through.
- Stop and rest if dizziness occurs.`,
    imageRef: "/exercises/neck-gentle-rotation.svg", // own placeholder illustration
    targetRegions: ["neck"],
    indications: ["neck strain", "cervical stiffness", "whiplash", "cervical muscle spasm", "overuse"],
    contraindications: ["suspected cervical fracture", "cervical instability", "vertebrobasilar insufficiency (dizziness with rotation)", "myelopathy"],
    frequencyRecommended: "2–3×/day, 10 slow reps each direction",
    expectedTimeToResults: "2–4 weeks",
    cautions: [
      "Stop immediately if dizziness occurs.",
      "Move only within pain-free range.",
      "Avoid if rotation is suspected to compress a nerve or artery.",
    ],
    evidenceNote: "DRAFT — needs clinical review before use",
    primaryMuscles: ["neck"],
    primaryMuscleIds: ["sternocleidomastoid", "splenius-capitis", "levator-scapulae"],
    secondaryMuscles: [],
    secondaryMuscleIds: [],
  },
];

/** Look up a single catalog exercise by its id. */
export function getPtExerciseById(id: string): PtExercise | undefined {
  return PT_EXERCISES.find((e) => e.id === id);
}

/** All exercises whose `targetRegions` include the given body region. */
export function findPtExercisesForRegion(region: string): PtExercise[] {
  return PT_EXERCISES.filter((e) => e.targetRegions.includes(region));
}
